import { parseJsonOrFallback } from './text';

/**
 * Per-tab storage backed by `window.name`.
 *
 * Purpose: support having a different workspace open in each browser tab
 * simultaneously. Each tab injects its own `selectedWorkspaceId` into API
 * requests (see `config/axios`), so the workspace context must be isolated
 * per tab.
 *
 * Why `window.name` and not localStorage / sessionStorage:
 * - localStorage is shared across all tabs, so switching workspace in one tab
 *   would clobber every other tab — no isolation.
 * - sessionStorage is per-tab but gets *copied* into new tabs on duplicate /
 *   open-in-new-tab, and doesn't reliably survive cross-origin (OAuth) redirects.
 * - `window.name` is truly bound to a single tab: it survives reloads and
 *   cross-origin redirects, and a brand-new tab always starts empty.
 */
export type TabStorageData = {
	selectedWorkspaceId?: string;
	/** Handle of the selected workspace, mirrored here so analytics can attach it per tab. */
	workspaceHandle?: string;
	isFreshLogin?: boolean;
};

class TabIsolation {
	private static instance: TabIsolation;

	private constructor() {}

	public static getInstance(): TabIsolation {
		if (!TabIsolation.instance) {
			TabIsolation.instance = new TabIsolation();
		}
		return TabIsolation.instance;
	}

	private getTabData(): TabStorageData {
		if (typeof window === 'undefined') return {};

		const storedData = window.name || '{}';
		return parseJsonOrFallback<TabStorageData>(storedData, {});
	}

	private saveTabData(data: TabStorageData): void {
		if (typeof window === 'undefined') return;

		window.name = JSON.stringify(data);
	}

	public get<K extends keyof TabStorageData>(key: K): TabStorageData[K] | undefined {
		const tabData = this.getTabData();
		return tabData[key];
	}

	public set<K extends keyof TabStorageData>(key: K, value: TabStorageData[K]): void {
		const tabData = this.getTabData();
		tabData[key] = value;
		this.saveTabData(tabData);
	}

	public clear(): void {
		window.name = '{}';
	}

	public remove<K extends keyof TabStorageData>(key: K): void {
		const tabData = this.getTabData();
		delete tabData[key];
		this.saveTabData(tabData);
	}

	public getAll(): TabStorageData {
		return this.getTabData();
	}
}

export const tabStorage = TabIsolation.getInstance();
